import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, DollarSign, Calendar, User, AlertCircle, CheckCircle, Clock } from 'lucide-react';

const RHFiniquitos = () => {
  const [filterStatus, setFilterStatus] = useState('Todos');

  // Datos de ejemplo de finiquitos
  const finiquitos = [
    {
      id: 1,
      empleado: "Roberto Flores Martínez",
      numeroEmpleado: "EMP-2015-005",
      fechaBaja: "2025-12-15",
      motivoBaja: "Renuncia voluntaria",
      antiguedad: "8 años 9 meses",
      conceptos: [
        { concepto: "Salario pendiente", monto: 15000 },
        { concepto: "Vacaciones no gozadas", monto: 8500 },
        { concepto: "Prima vacacional", monto: 2550 },
        { concepto: "Aguinaldo proporcional", monto: 7500 },
        { concepto: "Prima de antigüedad", monto: 45000 }
      ],
      totalFiniquito: 78550,
      status: "Pagado",
      fechaPago: "2025-12-20"
    },
    {
      id: 2,
      empleado: "Patricia Ruiz Gómez",
      numeroEmpleado: "EMP-2020-018",
      fechaBaja: "2026-01-10",
      motivoBaja: "Término de contrato",
      antiguedad: "4 años 1 mes",
      conceptos: [
        { concepto: "Salario pendiente", monto: 12000 },
        { concepto: "Vacaciones no gozadas", monto: 6000 },
        { concepto: "Prima vacacional", monto: 1800 },
        { concepto: "Aguinaldo proporcional", monto: 1000 },
        { concepto: "Prima de antigüedad", monto: 20000 }
      ],
      totalFiniquito: 40800,
      status: "En proceso",
      fechaPago: null
    },
    {
      id: 3,
      empleado: "Miguel Ángel Castro Pérez",
      numeroEmpleado: "EMP-2018-012",
      fechaBaja: "2026-01-25",
      motivoBaja: "Renuncia voluntaria",
      antiguedad: "6 años 0 meses",
      conceptos: [
        { concepto: "Salario pendiente", monto: 18000 },
        { concepto: "Vacaciones no gozadas", monto: 9000 },
        { concepto: "Prima vacacional", monto: 2700 },
        { concepto: "Aguinaldo proporcional", monto: 1500 },
        { concepto: "Prima de antigüedad", monto: 30000 }
      ],
      totalFiniquito: 61200,
      status: "Pendiente",
      fechaPago: null
    },
    {
      id: 4,
      empleado: "Sandra Jiménez Luna",
      numeroEmpleado: "EMP-2021-023",
      fechaBaja: "2025-11-30",
      motivoBaja: "Despido justificado",
      antiguedad: "3 años 2 meses",
      conceptos: [
        { concepto: "Salario pendiente", monto: 10000 },
        { concepto: "Vacaciones no gozadas", monto: 5000 },
        { concepto: "Prima vacacional", monto: 1500 },
        { concepto: "Aguinaldo proporcional", monto: 9166 }
      ],
      totalFiniquito: 25666,
      status: "Pagado",
      fechaPago: "2025-12-05"
    }
  ];

  const filteredFiniquitos = filterStatus === 'Todos' 
    ? finiquitos 
    : finiquitos.filter(f => f.status === filterStatus);

  const getStatusColor = (status) => {
    switch (status) {
      case 'Pagado':
        return 'bg-green-100 text-green-700 border-green-300';
      case 'En proceso':
        return 'bg-blue-100 text-blue-700 border-blue-300';
      case 'Pendiente':
        return 'bg-orange-100 text-orange-700 border-orange-300';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Pagado':
        return <CheckCircle className="w-5 h-5" />;
      case 'En proceso':
        return <Clock className="w-5 h-5" />;
      case 'Pendiente':
        return <AlertCircle className="w-5 h-5" />;
      default:
        return null;
    }
  };

  const totalPagado = finiquitos.filter(f => f.status === 'Pagado').reduce((sum, f) => sum + f.totalFiniquito, 0);
  const totalPendiente = finiquitos.filter(f => f.status !== 'Pagado').reduce((sum, f) => sum + f.totalFiniquito, 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-red-100 py-12 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-4 mb-8">
          <Link to="/recursos-humanos">
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              className="p-2 rounded-lg bg-white shadow-md hover:shadow-lg transition"
            >
              <ArrowLeft className="w-6 h-6 text-gray-700" />
            </motion.button>
          </Link>
          <h1 className="text-4xl font-bold text-gray-800">💰 Finiquitos y Liquidaciones</h1>
        </div>

        {/* Resumen financiero */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-lg p-6"
          >
            <div className="flex items-center justify-between mb-2">
              <p className="text-gray-500 text-sm">Total Procesados</p>
              <FileText className="w-6 h-6 text-orange-500" />
            </div>
            <p className="text-4xl font-bold text-gray-800">{finiquitos.length}</p>
            <p className="text-sm text-gray-500 mt-2">Últimos 6 meses</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl shadow-lg p-6"
          >
            <div className="flex items-center justify-between mb-2">
              <p className="text-gray-500 text-sm">Total Pagado</p>
              <CheckCircle className="w-6 h-6 text-green-500" />
            </div>
            <p className="text-4xl font-bold text-green-600">
              ${(totalPagado / 1000).toFixed(1)}K
            </p>
            <p className="text-sm text-gray-500 mt-2">MXN</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl shadow-lg p-6"
          >
            <div className="flex items-center justify-between mb-2">
              <p className="text-gray-500 text-sm">Total Pendiente</p>
              <AlertCircle className="w-6 h-6 text-orange-500" />
            </div>
            <p className="text-4xl font-bold text-orange-600">
              ${(totalPendiente / 1000).toFixed(1)}K
            </p>
            <p className="text-sm text-gray-500 mt-2">MXN</p>
          </motion.div>
        </div>

        {/* Filtros */}
        <div className="bg-white rounded-xl shadow-lg p-4 mb-6">
          <div className="flex gap-3">
            {['Todos', 'Pendiente', 'En proceso', 'Pagado'].map((status) => (
              <button
                key={status}
                onClick={() => setFilterStatus(status)}
                className={`px-4 py-2 rounded-lg font-semibold transition ${
                  filterStatus === status
                    ? 'bg-orange-500 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {status}
              </button>
            ))}
          </div>
        </div>

        {/* Lista de finiquitos */}
        <div className="space-y-6">
          {filteredFiniquitos.map((finiquito, index) => (
            <motion.div
              key={finiquito.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-xl shadow-lg p-6"
            >
              <div className="flex items-start justify-between mb-6">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-orange-400 to-orange-600 rounded-full flex items-center justify-center text-white text-xl font-bold">
                    {finiquito.empleado.split(' ').map(n => n[0]).join('').substring(0, 2)}
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-800">{finiquito.empleado}</h3>
                    <p className="text-gray-500 text-sm">{finiquito.numeroEmpleado}</p>
                    <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        <span>Baja: {new Date(finiquito.fechaBaja).toLocaleDateString('es-MX')}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        <span>{finiquito.antiguedad}</span>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      <span className="font-semibold">Motivo:</span> {finiquito.motivoBaja}
                    </p>
                  </div>
                </div>
                <div className={`flex items-center gap-2 px-4 py-2 rounded-lg border ${getStatusColor(finiquito.status)}`}>
                  {getStatusIcon(finiquito.status)}
                  <span className="font-semibold">{finiquito.status}</span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-700 mb-3">Conceptos del Finiquito</h4>
                  <div className="space-y-2">
                    {finiquito.conceptos.map((concepto, idx) => (
                      <div key={idx} className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">{concepto.concepto}</span>
                        <span className="font-semibold text-gray-800">
                          ${concepto.monto.toLocaleString('es-MX')}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-4 flex flex-col justify-between">
                  <div>
                    <h4 className="font-semibold text-gray-700 mb-2">Resumen</h4>
                    <div className="space-y-1 text-sm text-gray-600">
                      <p>Conceptos pagables: {finiquito.conceptos.length}</p>
                      {finiquito.fechaPago && (
                        <p>Fecha de pago: {new Date(finiquito.fechaPago).toLocaleDateString('es-MX')}</p>
                      )}
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-orange-200">
                    <p className="text-sm text-gray-600 mb-1">Total del Finiquito</p>
                    <p className="text-3xl font-bold text-orange-600">
                      ${finiquito.totalFiniquito.toLocaleString('es-MX')}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="flex-1 bg-orange-500 text-white py-2 rounded-lg hover:bg-orange-600 transition font-semibold"
                >
                  Ver Detalles Completos
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="flex items-center gap-2 bg-gray-500 text-white px-6 py-2 rounded-lg hover:bg-gray-600 transition font-semibold"
                >
                  <FileText className="w-4 h-4" />
                  Generar PDF
                </motion.button>
              </div>
            </motion.div>
          ))}
        </div>

        {filteredFiniquitos.length === 0 && (
          <div className="bg-white rounded-xl shadow-lg p-12 text-center">
            <p className="text-gray-500 text-lg">No hay finiquitos con el estado seleccionado.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default RHFiniquitos;
